import LineSearchIcon from 'LuxKit/Icons/line/LineSearchIcon'
import Modal from 'LuxKit/Modal/Modal'
import type { ChangeEventHandler } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import TextInput from '../TextInput'
import AddressInputOptions from './AddressInputOptions'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import type { GoogleAutocompletePlaceItem } from 'api/search'

interface Props {
  label: string
  defaultValue?: string
  placeholder?: string
  defaultPlaceId?: string
  originLatitude?: number
  originLongitude?: number
  /**
   * Radius around the latitude/longitude given to return search results for in KM
   * Defaults to 100km
   * @default 100
   */
  radius?: number
  /**
   * Custom filter function to filter autocomplete predictions
   */
  filterPredictions?: (predictions: Array<GoogleAutocompletePlaceItem>) => Array<GoogleAutocompletePlaceItem>
}

function AddressInputModal(props: Props) {
  const {
    label,
    defaultPlaceId,
    defaultValue = '',
    placeholder,
    originLatitude,
    originLongitude,
    radius = 100,
    filterPredictions,
  } = props

  const inputRef = useRef<HTMLInputElement>(null)
  const [searchPhrase, setSearchPhrase] = useState<string>(defaultValue)

  useEffect(() => {
    // auto focus the input as we open
    inputRef.current?.focus()
  }, [])

  const { resolve } = useModalElementContext<GoogleAutocompletePlaceItem | undefined>()

  const searchPhraseInputChange = useCallback<ChangeEventHandler<HTMLInputElement>>((event) => {
    setSearchPhrase(event.currentTarget.value)
  }, [])

  const onItemSelect = useCallback((option: GoogleAutocompletePlaceItem) => {
    resolve(option)
  }, [resolve])

  return <Modal
    height="max"
    title={label}
    headerExtension={<TextInput
      autoComplete="off"
      onChange={searchPhraseInputChange}
      endIcon={<LineSearchIcon />}
      defaultValue={searchPhrase}
      placeholder={placeholder}
      noValidationSpacing
      ref={inputRef}
    />}
  >
    <AddressInputOptions
      onSelect={onItemSelect}
      searchPhrase={searchPhrase}
      selectedPlaceId={defaultPlaceId}
      originLatitude={originLatitude}
      originLongitude={originLongitude}
      radius={radius}
      filterPredictions={filterPredictions}
    />
  </Modal>
}

export default AddressInputModal
